import { DocumentIcon, XIcon } from "@heroicons/react/outline";
import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { cancelStorageRequest, formatFileSize } from "../../../Utils";

const UploadFile = ({
  title,
  file,
  setFile,
  handleDragEnter,
  handleDragLeave,
  handleDragOver,
  handleDrop,
  handleFileChange,
  handleFileSelect,
  fileInputRef,
  isDragActive,
  fieldErrorFile,
  uploadLoading,
  storageRequest,
  progressUpload,
}) => {
  const { t } = useTranslation();

  const renderProgressBar = (percentage) => (
    <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
      <div
        className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-1 leading-none rounded-full"
        style={{ width: `${percentage}%` }}
      />
    </div>
  );

  return (
    <>
      <div className="flex flex-col mb-6">
        <span className="text-sm text-gray-900 mb-2">{t(title)}</span>
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={clsx(
            isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300",
            fieldErrorFile.status && !isDragActive && "border-red-500",
            "border-2 border-dashed rounded-lg p-6 text-center",
          )}
        >
          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx"
            className="hidden"
            onChange={handleFileChange}
          />
          <div className="flex flex-col items-center justify-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="text-sm text-gray-600 mb-2">
              <button
                type="button"
                onClick={handleFileSelect}
                className="font-medium text-blue-600 hover:bg-gray-50 mr-2"
              >
                {t("transactions.modal.actions.choose")}
              </button>
              <span>{t("transactions.modal.dragDrop")}</span>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {t("transactions.modal.fileFormat")}: XLSX
            </p>
            {fieldErrorFile.status && (
              <span className="text-sm text-red-600">
                {t(fieldErrorFile.message)}
              </span>
            )}
          </div>
        </div>
      </div>

      {file && (
        <div className="flex border border-gray-300 rounded-lg py-4 px-2">
          <div className="flex flex-1 min-w-0 items-center">
            <DocumentIcon className="w-12 h-12 text-gray-400" />
            <div className="flex flex-col flex-1 min-w-0 text-sm text-gray-700">
              <span className="line-clamp-2 break-all sm:line-clamp-1 sm:break-normal sm:truncate">
                {file.name}
              </span>
              <span>{formatFileSize(file.size)}</span>
              {uploadLoading
                ? storageRequest !== null
                  ? renderProgressBar(
                      Math.round(
                        (progressUpload?.transferredBytes /
                          progressUpload?.totalBytes) *
                          100,
                      ),
                      100,
                    )
                  : renderProgressBar(100)
                : null}
            </div>
          </div>
          <div className="flex flex-col justify-between ml-3">
            <button
              type="button"
              onClick={() => {
                setFile(null);
                if (storageRequest) {
                  cancelStorageRequest(storageRequest);
                }
              }}
            >
              <XIcon className="w-5 h-5 text-gray-400 hover:text-gray-500" />
            </button>
            {uploadLoading && (
              <div className="text-sm text-gray-700 mt-5">
                {Math.round(
                  progressUpload?.transferredBytes && progressUpload?.totalBytes
                    ? (progressUpload.transferredBytes /
                        progressUpload.totalBytes) *
                        100
                    : 0,
                )}
                %
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UploadFile;
